import { Subject } from 'rxjs';
import {
  AppointmentPermission,
  DataBreachPermission,
  DataProtectionGovernancePermission,
  DocumentPermission,
  PtPermissions,
} from '@pt/pt-models';
import { NgxPermissionsService } from 'ngx-permissions';
import { DocAccess } from 'it/primatoo/openapi/api/org/schemas/common/DocAccess';

import { ArchiveStatus } from 'it/primatoo/openapi/api/org/schemas/databreachprocess/ArchiveStatus';
import { Level } from 'it/primatoo/openapi/api/org/schemas/databreachprocess/Level';
import { Injectable } from '@angular/core';
import { DocOperation } from 'it/primatoo/openapi/api/org/schemas/common/DocOperation';
import { DataBreach as DataBreachFindAll } from 'it/primatoo/openapi/api/org/schemas/databreachprocess/listdatabreachresponse/DataBreach';
import { AvailableOperations } from 'it/primatoo/openapi/api/org/schemas/common/AvailableOperations';
import { DataBreach } from 'it/primatoo/openapi/api/org/schemas/databreachprocess/DataBreach';
import { Chapter } from 'it/primatoo/openapi/api/org/schemas/common/docs/_listtemplatechapterresponse/Chapter';

@Injectable()
export class PermissionManager {
  private _currentPermission: PtPermissions[] = [];
  private _activeChapter?: Chapter;
  private destroy$ = new Subject<boolean>();

  static readAccessRequired(docAccess: DocAccess): boolean {
    return (
      docAccess === DocAccess.FULL ||
      docAccess === DocAccess.WRITE ||
      docAccess === DocAccess.READ_ONLY
    );
  }

  static writeAccessRequired(docAccess: DocAccess): boolean {
    return docAccess === DocAccess.FULL || docAccess === DocAccess.WRITE;
  }

  constructor(private permissionsService: NgxPermissionsService) {}

  public setActiveChapter(chapter?: Chapter) {
    this._activeChapter = chapter;
  }

  public alignActivePermission(activePermission: PtPermissions[]) {
    if (
      JSON.stringify(this._currentPermission) !==
      JSON.stringify(activePermission)
    ) {
      this._currentPermission = activePermission;
      this.permissionsService.flushPermissions();
      this.permissionsService.loadPermissions(activePermission);
    }
  }

  public appointmentPermission(): AppointmentPermission {
    const hasDPGPermission: boolean = this._currentPermission.includes(
      PtPermissions.DPG
    );
    return {
      editBtnEnabled: true,
      editBtnVisible: hasDPGPermission,

      detailBtnEnabled: true,
      detailBtnVisible: true,

      deleteBtnEnabled: true,
      deleteBtnVisible: hasDPGPermission,
    };
  }

  // DOCUMENTI
  public treatmentPermission(
    availableOperations: AvailableOperations
  ): DocumentPermission {
    const docAccess: DocAccess =
      availableOperations.access || DocAccess.READ_ONLY;
    const canPublish: boolean = availableOperations.operations.includes(
      DocOperation.PUBLISH
    );
    const canReopen: boolean = availableOperations.operations.includes(
      DocOperation.REOPEN
    );
    const canValidate: boolean = availableOperations.operations.includes(
      DocOperation.VALIDATE
    );
    const canCreateTreatment: boolean = this._currentPermission.includes(
      PtPermissions.CAN_CREATE_TREAT
    );
    const canManageWorkspace = this._currentPermission.includes(
      PtPermissions.MANAGE_WORKSPACE
    );
    const canDelete: boolean = availableOperations.operations.includes(
      DocOperation.DELETE
    );
    const canCreateDpiaComment: boolean =
      availableOperations.operations.includes(DocOperation.DPIA_COMMENT);
    const canCreateObsolete: boolean = availableOperations.operations.includes(
      DocOperation.OBSOLETE
    );

    const canCreateSnapshot: boolean = availableOperations.operations.includes(
      DocOperation.SNAPSHOT
    );
    return {
      snapshotBtnVisible: true,
      snapshotBtnEnabled: canCreateSnapshot,
      operations: availableOperations.operations,

      obsoleteBtnEnabled: canCreateObsolete,
      obsoleteBtnVisible: true,

      manageWorkspaceAccess: canManageWorkspace,
      writeAccess: PermissionManager.writeAccessRequired(docAccess),

      openBtnEnabled: PermissionManager.readAccessRequired(docAccess),
      openBtnVisible: true,

      detailBtnEnabled: PermissionManager.readAccessRequired(docAccess),
      detailBtnVisible: true,

      reopenBtnEnabled: canReopen, //  && docStatus === DocResourceStatus.PUBLISHED && !doc.validated,
      reopenBtnVisible: true,

      publishBtnEnabled: canPublish, // && docStatus !== DocResourceStatus.PUBLISHED && !doc.validated && doc.completed === 100,
      publishBtnVisible: true,

      validateBtnEnabled: canValidate, // && docStatus === DocResourceStatus.PUBLISHED && !doc.validated,
      validateBtnVisible: true,

      duplicateBtnEnabled:
        PermissionManager.readAccessRequired(docAccess) && canCreateTreatment,
      duplicateBtnVisible: true,

      permissionBtnEnabled: PermissionManager.readAccessRequired(docAccess),
      permissionBtnVisible: true,

      downloadBtnEnabled: PermissionManager.readAccessRequired(docAccess),
      downloadBtnVisible: true,

      deleteBtnEnabled: canDelete,
      deleteBtnVisible: true,

      reminderBtnEnabled: true,
      reminderBtnVisible: true,

      archiveBtnVisible: false,
      archiveBtnEnabled: false,

      dpiaCommentBtnVisible: true,
      dpiaCommentBtnEnabled: canCreateDpiaComment,

      closeBtnVisible: false,
      closeBtnEnabled: false,
    };
  }

  public globalMeasureCheckPermission(
    availableOperations: AvailableOperations
  ): DocumentPermission {
    const docAccess: DocAccess =
      availableOperations.access || DocAccess.READ_ONLY;
    const canPublish: boolean = availableOperations.operations.includes(
      DocOperation.PUBLISH
    );
    const canReopen: boolean = availableOperations.operations.includes(
      DocOperation.REOPEN
    );
    const canValidate: boolean = availableOperations.operations.includes(
      DocOperation.VALIDATE
    );

    // const canCreateRiskAssessment: boolean = this._currentPermission.includes(PtPermissions.CAN_CREATE_RISK_AS);
    const canManageWorkspace = this._currentPermission.includes(
      PtPermissions.MANAGE_WORKSPACE
    );
    const canDelete: boolean = availableOperations.operations.includes(
      DocOperation.DELETE
    );
    const canCreateSnapshot: boolean = availableOperations.operations.includes(
      DocOperation.SNAPSHOT
    );

    return {
      snapshotBtnVisible: true,
      snapshotBtnEnabled: canCreateSnapshot,
      operations: availableOperations.operations,

      manageWorkspaceAccess: canManageWorkspace,
      writeAccess: PermissionManager.writeAccessRequired(docAccess),

      openBtnEnabled: PermissionManager.readAccessRequired(docAccess),
      openBtnVisible: true,

      reopenBtnEnabled: canReopen,
      reopenBtnVisible: true,

      publishBtnEnabled: canPublish,
      publishBtnVisible: true,

      validateBtnEnabled: canValidate,
      validateBtnVisible: true,

      duplicateBtnEnabled: PermissionManager.readAccessRequired(docAccess), // && canCreateRiskAssessment,
      duplicateBtnVisible: true,

      permissionBtnEnabled: PermissionManager.readAccessRequired(docAccess),
      permissionBtnVisible: true,

      downloadBtnEnabled: PermissionManager.readAccessRequired(docAccess),
      downloadBtnVisible: true,

      detailBtnEnabled: PermissionManager.readAccessRequired(docAccess),
      detailBtnVisible: true,

      deleteBtnEnabled: canDelete,
      deleteBtnVisible: true,

      reminderBtnEnabled: true,
      reminderBtnVisible: true,

      archiveBtnVisible: false,
      archiveBtnEnabled: false,

      dpiaCommentBtnVisible: false,
      dpiaCommentBtnEnabled: false,

      closeBtnVisible: false,
      closeBtnEnabled: false,

      obsoleteBtnVisible: false,
      obsoleteBtnEnabled: false,
    };
  }

  public aiAssessmentPermission(
    availableOperations: AvailableOperations
  ): DocumentPermission {
    const activeModuleAi: boolean = this._currentPermission.includes(
      PtPermissions.ACTIVE_MODULE_AI
    );
    const docAccess: DocAccess = activeModuleAi
      ? availableOperations?.access || DocAccess.READ_ONLY
      : DocAccess.READ_ONLY;
    const canPublish: boolean =
      activeModuleAi &&
      availableOperations?.operations.includes(DocOperation.PUBLISH);
    const canReopen: boolean =
      activeModuleAi &&
      availableOperations?.operations.includes(DocOperation.REOPEN);
    const canValidate: boolean =
      activeModuleAi &&
      availableOperations?.operations.includes(DocOperation.VALIDATE);
    const canCreateAiAssessment: boolean =
      activeModuleAi &&
      this._currentPermission.includes(PtPermissions.CAN_CREATE_AI_AS) &&
      this._currentPermission.includes(PtPermissions.ACTIVE_MODULE_AI);
    const canManageWorkspace = this._currentPermission.includes(
      PtPermissions.MANAGE_WORKSPACE
    );
    const canDelete: boolean = availableOperations.operations.includes(
      DocOperation.DELETE
    );
    const canCreateSnapshot: boolean = availableOperations.operations.includes(
      DocOperation.SNAPSHOT
    );

    return {
      snapshotBtnVisible: true,
      snapshotBtnEnabled: canCreateSnapshot,
      operations: availableOperations.operations,

      manageWorkspaceAccess: canManageWorkspace,
      writeAccess: PermissionManager.writeAccessRequired(docAccess),

      openBtnEnabled: PermissionManager.readAccessRequired(docAccess),
      openBtnVisible: true,

      reopenBtnEnabled: canReopen,
      reopenBtnVisible: true,

      publishBtnEnabled: canPublish,
      publishBtnVisible: true,

      validateBtnEnabled: canValidate,
      validateBtnVisible: true,

      duplicateBtnEnabled:
        PermissionManager.readAccessRequired(docAccess) &&
        canCreateAiAssessment,
      duplicateBtnVisible: true,

      permissionBtnEnabled: PermissionManager.readAccessRequired(docAccess),
      permissionBtnVisible: true,

      downloadBtnEnabled: PermissionManager.readAccessRequired(docAccess),
      downloadBtnVisible: true,

      detailBtnEnabled: PermissionManager.readAccessRequired(docAccess),
      detailBtnVisible: true,

      deleteBtnEnabled: canDelete,
      deleteBtnVisible: true,

      reminderBtnEnabled: true,
      reminderBtnVisible: true,

      archiveBtnVisible: false,
      archiveBtnEnabled: false,

      dpiaCommentBtnVisible: false,
      dpiaCommentBtnEnabled: false,

      closeBtnVisible: false,
      closeBtnEnabled: false,

      obsoleteBtnVisible: false,
      obsoleteBtnEnabled: false,
    };
  }

  public covidAssessmentPermission(
    availableOperations: AvailableOperations
  ): DocumentPermission {
    const activeModuleCovid: boolean = this._currentPermission.includes(
      PtPermissions.ACTIVE_MODULE_COVID
    );

    const docAccess: DocAccess = activeModuleCovid
      ? availableOperations?.access || DocAccess.READ_ONLY
      : DocAccess.READ_ONLY;

    const canReopen: boolean =
      activeModuleCovid &&
      availableOperations?.operations?.includes(DocOperation.REOPEN);
    const canPublish: boolean =
      activeModuleCovid &&
      availableOperations?.operations?.includes(DocOperation.PUBLISH);
    const canValidate: boolean =
      activeModuleCovid &&
      availableOperations?.operations?.includes(DocOperation.VALIDATE);

    const canCreateCovidAssessment: boolean =
      activeModuleCovid &&
      this._currentPermission.includes(PtPermissions.CAN_CREATE_COVID_AS) &&
      this._currentPermission.includes(PtPermissions.ACTIVE_MODULE_COVID);
    const canManageWorkspace = this._currentPermission.includes(
      PtPermissions.MANAGE_WORKSPACE
    );
    const canDelete: boolean = availableOperations?.operations?.includes(
      DocOperation.DELETE
    );
    const canCreateSnapshot: boolean =
      availableOperations?.operations?.includes(DocOperation.SNAPSHOT);

    return {
      snapshotBtnVisible: true,
      snapshotBtnEnabled: canCreateSnapshot,

      operations: availableOperations?.operations,

      manageWorkspaceAccess: canManageWorkspace,
      writeAccess: PermissionManager.writeAccessRequired(docAccess),

      openBtnEnabled: PermissionManager.readAccessRequired(docAccess),
      openBtnVisible: true,

      reopenBtnEnabled: canReopen,
      reopenBtnVisible: true,

      publishBtnEnabled: canPublish,
      publishBtnVisible: true,

      validateBtnEnabled: canValidate,
      validateBtnVisible: true,

      duplicateBtnEnabled:
        PermissionManager.readAccessRequired(docAccess) &&
        canCreateCovidAssessment,
      duplicateBtnVisible: true,

      permissionBtnEnabled: PermissionManager.readAccessRequired(docAccess),
      permissionBtnVisible: true,

      downloadBtnEnabled: PermissionManager.readAccessRequired(docAccess),
      downloadBtnVisible: true,

      detailBtnEnabled: PermissionManager.readAccessRequired(docAccess),
      detailBtnVisible: true,

      deleteBtnEnabled: canDelete,
      deleteBtnVisible: true,

      reminderBtnEnabled: true,
      reminderBtnVisible: true,

      archiveBtnVisible: false,
      archiveBtnEnabled: false,

      dpiaCommentBtnVisible: false,
      dpiaCommentBtnEnabled: false,

      closeBtnVisible: false,
      closeBtnEnabled: false,

      obsoleteBtnVisible: false,
      obsoleteBtnEnabled: false,
    };
  }

  public dpgPermission(doc): DataProtectionGovernancePermission {
    return {
      dpgPermission: this._currentPermission.includes(PtPermissions.DPG),
      requiredBuiltIn: doc?.builtIn,
      system: this._currentPermission.includes(
        PtPermissions.ENABLE_FOR_SYSTEM || PtPermissions.ENABLE_FOR_ADMIN
      ),
    };
  }

  public appAssessmentPermissionV2(
    availableOperations: AvailableOperations
  ): DocumentPermission {
    const docAccess: DocAccess =
      availableOperations?.access || DocAccess.READ_ONLY;
    const canPublish: boolean = availableOperations?.operations.includes(
      DocOperation.PUBLISH
    );
    const canReopen: boolean = availableOperations?.operations.includes(
      DocOperation.REOPEN
    );
    const canValidate: boolean = availableOperations?.operations.includes(
      DocOperation.VALIDATE
    );
    const canCreateAppAssessment = this._currentPermission.includes(
      PtPermissions.CAN_CREATE_APP_AS
    );
    const canManageWorkspace = this._currentPermission.includes(
      PtPermissions.MANAGE_WORKSPACE
    );
    const canDelete: boolean = availableOperations.operations.includes(
      DocOperation.DELETE
    );
    const canCreateSnapshot: boolean = availableOperations.operations.includes(
      DocOperation.SNAPSHOT
    );

    return {
      snapshotBtnVisible: true,
      snapshotBtnEnabled: canCreateSnapshot,

      operations: availableOperations.operations,

      manageWorkspaceAccess: canManageWorkspace,
      writeAccess: PermissionManager.writeAccessRequired(docAccess),

      openBtnEnabled: PermissionManager.readAccessRequired(docAccess),
      openBtnVisible: true,

      reopenBtnEnabled: canReopen,
      reopenBtnVisible: true,

      publishBtnEnabled: canPublish,
      publishBtnVisible: true,

      validateBtnEnabled: canValidate,
      validateBtnVisible: true,

      duplicateBtnEnabled:
        PermissionManager.readAccessRequired(docAccess) &&
        canCreateAppAssessment,
      duplicateBtnVisible: true,

      permissionBtnEnabled: PermissionManager.readAccessRequired(docAccess),
      permissionBtnVisible: true,

      downloadBtnEnabled: PermissionManager.readAccessRequired(docAccess),
      downloadBtnVisible: true,

      detailBtnEnabled: PermissionManager.readAccessRequired(docAccess),
      detailBtnVisible: true,

      deleteBtnEnabled: canDelete,
      deleteBtnVisible: true,

      reminderBtnEnabled: true,
      reminderBtnVisible: true,

      archiveBtnVisible: false,
      archiveBtnEnabled: false,

      dpiaCommentBtnVisible: false,
      dpiaCommentBtnEnabled: false,

      closeBtnVisible: false,
      closeBtnEnabled: false,

      obsoleteBtnVisible: false,
      obsoleteBtnEnabled: false,
    };
  }

  public dataBreachFindAllPermission(
    doc: DataBreachFindAll | DataBreach,
    availableOperations: AvailableOperations
  ): DataBreachPermission {
    const request: DataBreachPermissionInfo = {
      operations: availableOperations,
      deletionAt: doc.deletionAt,
      archived: doc.archived,
      archivedAt: doc.archivedAt,
      level: doc.level,
    };
    return this.dataBreachPermission(request);
  }

  private dataBreachPermission(
    info: DataBreachPermissionInfo
  ): DataBreachPermission {
    const activeModuleDataBreach: boolean = this._currentPermission.includes(
      PtPermissions.ACTIVE_MODULE_DATABREACH
    );
    const canManageDataBreach: boolean = this._currentPermission.includes(
      PtPermissions.MANAGE_DATABREACH
    );

    const activeDataBreach = activeModuleDataBreach && canManageDataBreach;

    const docAccess: DocAccess = activeDataBreach
      ? info.operations.access
      : DocAccess.READ_ONLY;
    const canOpen: boolean =
      activeDataBreach &&
      (info.operations.operations.includes(DocOperation.DTB_OPEN) ||
        info.level === Level.OPEN);
    const canClose: boolean =
      activeDataBreach &&
      info.operations.operations.includes(DocOperation.DTB_CLOSE);
    const canDelete: boolean =
      activeDataBreach &&
      info.operations.operations.includes(DocOperation.DTB_DELETE);
    const canCancelDelete: boolean =
      activeDataBreach &&
      info.operations.operations.includes(DocOperation.DTB_CANCEL_DELETE);
    const canCreateDataBreach =
      activeDataBreach &&
      this._currentPermission.includes(PtPermissions.CAN_CREATE_DATABREACH);
    const canManageWorkspace = this._currentPermission.includes(
      PtPermissions.MANAGE_WORKSPACE
    );

    return {
      snapshotBtnVisible: false,
      snapshotBtnEnabled: false,
      operations: info.operations.operations,
      manageWorkspaceAccess: canManageWorkspace,
      writeAccess: PermissionManager.writeAccessRequired(docAccess),
      dpgPermission: activeDataBreach,

      createBtnEnabled: canCreateDataBreach,
      createBtnVisible: true,

      deleteBtnEnabled: canDelete,
      deleteBtnVisible: true,

      cancelDeleteBtnEnabled: canCancelDelete,
      cancelDeleteBtnVisible: true,

      openBtnEnabled: canOpen,
      openBtnVisible: true,

      reopenBtnEnabled: false,
      reopenBtnVisible: false,

      publishBtnEnabled: false,
      publishBtnVisible: false,

      validateBtnEnabled: false,
      validateBtnVisible: false,

      duplicateBtnEnabled: false,
      duplicateBtnVisible: false,

      permissionBtnEnabled: PermissionManager.readAccessRequired(docAccess),
      permissionBtnVisible: true,

      downloadBtnEnabled: PermissionManager.readAccessRequired(docAccess),
      downloadBtnVisible: true,

      detailBtnEnabled: PermissionManager.readAccessRequired(docAccess),
      detailBtnVisible: true,

      archiveBtnEnabled: false,
      archiveBtnVisible: false,

      reminderBtnEnabled: false,
      reminderBtnVisible: false,

      dpiaCommentBtnVisible: false,
      dpiaCommentBtnEnabled: false,

      closeBtnEnabled: canClose,
      closeBtnVisible: true,

      obsoleteBtnVisible: false,
      obsoleteBtnEnabled: false,
    };
  }

  public dataBreachPermission2(
    info: AvailableOperations
  ): DataBreachPermission {
    const activeModuleDataBreach: boolean = this._currentPermission.includes(
      PtPermissions.ACTIVE_MODULE_DATABREACH
    );
    const canManageDataBreach: boolean = this._currentPermission.includes(
      PtPermissions.MANAGE_DATABREACH
    );

    const activeDataBreach = activeModuleDataBreach && canManageDataBreach;

    const docAccess: DocAccess = activeDataBreach
      ? info.access
      : DocAccess.READ_ONLY;
    const canOpen: boolean =
      activeDataBreach &&
      !info.operations.includes(DocOperation.DTB_CANCEL_DELETE);
    const canClose: boolean =
      activeDataBreach && info.operations.includes(DocOperation.DTB_CLOSE);
    const canDelete: boolean =
      activeDataBreach && info.operations.includes(DocOperation.DTB_DELETE);
    const canCancelDelete: boolean =
      activeDataBreach &&
      info.operations.includes(DocOperation.DTB_CANCEL_DELETE);
    const canCreateDataBreach =
      activeDataBreach &&
      this._currentPermission.includes(PtPermissions.CAN_CREATE_DATABREACH);
    const canManageWorkspace = this._currentPermission.includes(
      PtPermissions.MANAGE_WORKSPACE
    );

    return {
      snapshotBtnVisible: false,
      snapshotBtnEnabled: false,

      operations: info.operations,
      manageWorkspaceAccess: canManageWorkspace,
      writeAccess: PermissionManager.writeAccessRequired(docAccess),
      dpgPermission: activeDataBreach,

      createBtnEnabled: canCreateDataBreach,
      createBtnVisible: true,

      deleteBtnEnabled: canDelete,
      deleteBtnVisible: true,

      cancelDeleteBtnEnabled: canCancelDelete,
      cancelDeleteBtnVisible: canCancelDelete,

      openBtnEnabled: canOpen,
      openBtnVisible: true,

      reopenBtnEnabled: false,
      reopenBtnVisible: false,

      publishBtnEnabled: false,
      publishBtnVisible: false,

      validateBtnEnabled: false,
      validateBtnVisible: false,

      duplicateBtnEnabled: false,
      duplicateBtnVisible: false,

      permissionBtnEnabled: PermissionManager.readAccessRequired(docAccess),
      permissionBtnVisible: true,

      downloadBtnEnabled: PermissionManager.readAccessRequired(docAccess),
      downloadBtnVisible: true,

      detailBtnEnabled: PermissionManager.readAccessRequired(docAccess),
      detailBtnVisible: true,

      reminderBtnEnabled: false,
      reminderBtnVisible: false,

      dpiaCommentBtnVisible: false,
      dpiaCommentBtnEnabled: false,

      closeBtnEnabled: canClose,
      closeBtnVisible: true,

      archiveBtnEnabled: false,
      archiveBtnVisible: false,

      obsoleteBtnVisible: false,
      obsoleteBtnEnabled: false,
    };
  }

  public stop() {
    this.destroy$.next(true);
  }

  get currentPermission(): PtPermissions[] {
    return this._currentPermission || [];
  }
}

interface DataBreachPermissionInfo {
  operations: AvailableOperations;
  deletionAt?: number;
  archived: ArchiveStatus;
  archivedAt?: number;
  level: Level;
}
